type Generated = boolean;
type Accumulator = {
  [key: string]: any;
};
type Option = {
  label: string;
  value: string;
};

export const isStringifiedJSON = (obj: any) => {
  if (typeof obj === 'object') {
    return false;
  }
  if (obj === 'true' || obj === 'false') {
    return false;
  }
  try {
    const val = JSON.parse(obj);
    const isNumber = typeof val === 'number';
    return !isNumber && val !== null;
  } catch {
    return false;
  }
};

export const getValueForString = (obj: any) => {
  if (isStringifiedJSON(obj)) {
    return JSON.parse(obj);
  } else {
    return obj;
  }
};

/**
 * The function converts an optionsOject [key]:value to value/label array of objects.
 * @param optionsObject {Record<string, string>}
 * @returns {Option[]}
 */
export const convertRecordToOptionList = (
  optionsObject: Record<string, string>
): Option[] => {
  return Object.entries(optionsObject).map(([value, label]) => ({
    value,
    label
  }));
};

/**
 * The function converts an object list to value/label array of objects.
 * @param optionsObject {T[]}
 * @returns {Option[]}
 */
export const convertObjecListToOptionList = <T>(
  optionsObject: T[],
  keyField: keyof T & string,
  labelField: keyof T & string
): Option[] => {
  return optionsObject.map(
    item =>
      ({
        value: (item[keyField] as unknown) as string,
        label: (item[labelField] as unknown) as string
      } as Option)
  );
};

// ----------------------------------
// lodash style functions
// ----------------------------------

export const get = (obj: object, path: string, defaultValue = undefined) => {
  const travel = (regexp: RegExp) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      .reduce(
        (res: Accumulator, key) =>
          res !== null && res !== undefined ? res[key] : res,
        obj
      );
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
  return result === undefined || result === obj ? defaultValue : result;
};

/**
 * Performs a deep comparison between two values to determine if they are equivalent.
 *
 * @param {*} value - The value to compare.
 * @param {*} other - The other value to compare.
 * @returns {boolean} - Returns `true` if the values are equivalent, else `false`.
 */

export const isEqual = (x: any, y: any): boolean => {
  const ok = Object.keys,
    tx = typeof x,
    ty = typeof y;
  return x && y && tx === 'object' && tx === ty
    ? ok(x).length === ok(y).length &&
        ok(x).every(key => isEqual(x[key], y[key]))
    : x === y;
};

/**
 * Checks if value is an empty object or collection or string.
 *
 * @param {*} value - The value to compare.
 * @returns {boolean} - Returns `true` if the value is an empty object or collection, else `false`.
 */
export const isEmpty = (value: any): boolean => {
  return (
    value == null ||
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0)
  );
};

/**
 * Removes duplicate values from an array.
 *
 * @param {Array} arr - The array to inspect.
 * @returns {Array} - Returns the new duplicate-free array.
 */
export const uniq = (arr: any[]) => {
  return [...new Set(arr)];
};

/**
 * Removes duplicate values from an array except that it accepts iteratee which is invoked for each element in array to generate the criterion by which uniqueness is computed.
 *
 * @param {Array} arr - The array to inspect.
 * @param {Function} fnBy - The iteratee invoked per element.
 * @returns {Array} - Returns the new duplicate-free array.
 */
export const uniqBy = (arr: any, fnBy: any) => {
  return arr.filter((el, index) => {
    return (
      arr.findIndex(step => {
        return fnBy
          ? typeof fnBy === 'function'
            ? fnBy(el) === fnBy(step)
            : el[fnBy] === step[fnBy]
          : el === step;
      }) === index
    );
  });
};

/**
 * Similar to uniq except that it accepts a comparator which is invoked to compare elements of array
 *
 * @param {Array} arr - The value to check.
 * @param {function} fn - the comparator function
 * @returns {Array} - Returns the new duplicate free array.
 */
export const uniqWith = (arr: any, fn: any) => {
  return arr.filter((el: any, index: any) => {
    return (
      arr.findIndex((step: any) => {
        return fn ? fn(el, step) : el[fn] === step[fn];
      }) === index
    );
  });
};

/**
 * Checks if `value` is classified as a string primitive.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - Returns `true` if `value` is a string, else `false`.
 */
export const isString = (str: any) => {
  if (str != null && typeof str.valueOf() === 'string') {
    return true;
  }
  return false;
};

/**
 * Checks if `value` is classified as a number primitive or object.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - Returns `true` if `value` is a number, else `false`.
 */
export const isNumber = (num: any): boolean => {
  if (num != null && typeof num.valueOf() === 'number') {
    return true;
  }
  return false;
};

/**
 * Converts the first character of string to upper case and the remaining to lower case.
 *
 * @param {string} string - The string to capitalize.
 * @returns {string} - Returns the capitalized string.
 */
export const capitalize = (str?: string | null) => {
  return str
    ? str?.charAt(0)?.toUpperCase() + str?.slice(1)?.toLowerCase()
    : '';
};

/**
 * Converts the first character of string to upper case.
 *
 * @param {string} string - The string to convert.
 * @returns {string} - Returns the converted string.
 */
export const upperFirst = string => {
  return string ? string.charAt(0).toUpperCase() + string.slice(1) : '';
};

/**
 * Converts string to start case.
 *
 * @param {string | undefined} str - The string to modify.
 * @returns {string} - Returns string converted to startCase.
 */
export const startCase = (str: string | undefined) => {
  return str
    ? // regular expression to match words and capitalize the first letter of each
      str
        .replace(/([a-zA-Z0-9]+(?:['’][a-zA-Z0-9]+)?)|([a-zA-Z]+)/g, match => {
          return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
        })
        .replace(/[_-]+/g, ' ') // Replace underscores or hyphens with spaces
        .trim()
    : '';
};

/**
 * Truncates string if it's longer than the given maximum string length. The last characters of the truncated string are replaced with the omission string which defaults to "...".
 *
 * @param {string} str - The string to truncate.
 * @param {number} length - The maximum string length.
 * @param {string} separator - The separator pattern to truncate to.
 * @returns {string} - Returns the truncated string.
 */
export const truncate = (
  str: string,
  length: number,
  separator: string = '...'
) => {
  if (!str || length <= 0) {
    return '';
  }

  if (str.length <= length!) {
    return str;
  }

  // Max length is length minus the length of omission ...
  const maxLength = length - separator.length;

  if (separator) {
    const lastIndex = str.lastIndexOf(separator, maxLength);
    if (lastIndex !== -1) {
      return str.slice(0, lastIndex) + separator;
    }
  }

  return str.slice(0, maxLength) + separator;
};

/**
 * This method is like _.union except that it accepts iteratee which is invoked for each element of each arrays to generate the criterion by which uniqueness is computed.
 *
 * @param {...Array} arrays - The arrays to inspect.
 * @param {Function} iteratee - The iteratee invoked per element.
 * @returns {string} - Returns the new array of combined values.
 */
export const unionBy = (...arrays) => {
  const iteratee = arrays.pop();

  if (Array.isArray(iteratee)) {
    return [];
  }

  return [...arrays]
    .flat()
    .filter(
      (set => o => (set.has(iteratee(o)) ? false : set.add(iteratee(o))))(
        new Set()
      )
    );
};

/**
 * Creates an object composed of the picked object properties.
 *
 * @param {Object} object - The source object.
 * @param {string|string[]} keys - The property paths to pick..
 * @returns {string} - Returns the new object.
 */
export const pick = (object, keys) => {
  return keys.reduce((obj, key) => {
    if (object && object.hasOwnProperty(key)) {
      obj[key] = object[key];
    }
    return obj;
  }, {});
};

/**
 * Sets the value at `path` of `object`. If a portion of `path` doesn't exist, it's created.
 *
 * @param {Object} object - The object to modify.
 * @param {Array|string} path - The path of the property to set.
 * @param {*} value - The value to set.
 * @returns {Object} - Returns `object`.
 */
export const set = (obj: any, path: any, value: any) => {
  if (obj == null) {
    return obj;
  }

  const keys = Array.isArray(path) ? path : parsePath(path);
  let current = obj;

  keys.forEach((key: any, index: any) => {
    if (index === keys.length - 1) {
      current[key] = value;
    } else {
      // key does not exist, create
      if (!current[key]) {
        current[key] = {};
      }
      current = current[key];
    }
  });

  return obj;
};

type GroupByFunction<T> = (item: T) => string | number;
type GroupByKey<T> = keyof T | string | GroupByFunction<T>;

// helper function to get a value from an object using a dot notation string
const getValue = <T>(obj: T, path: string): any => {
  return path
    .split('.')
    .reduce((acc, key) => (acc ? acc[key] : undefined), obj);
};

/**
 * Creates an object composed of keys generated from the results of running each element of collection through
 * iteratee. The corresponding value of each key is an array of the elements responsible for generating the
 * key.
 *
 * @param collection The collection to iterate over.
 * @param iteratee The iteratee to transform keys.
 * @return Returns the composed aggregate object.
 */
export const groupBy = <T>(
  collection: T[],
  iteratee: GroupByKey<T>
): Record<string, T[]> => {
  const result: Record<string, T[]> = {};

  collection?.forEach(item => {
    // Determine the key using either a function, a property name, or a deep path
    let key: string | number;

    if (typeof iteratee === 'function') {
      key = iteratee(item);
    } else if (typeof iteratee === 'string') {
      key = getValue(item, iteratee) ?? '';
    } else {
      key = (item[iteratee] as unknown) as string | number;
    }

    const keyString = String(key);

    if (!result[keyString]) {
      result[keyString] = [];
    }

    result[keyString].push(item);
  });

  return result;
};

// a helper function to parse paths and correctly interpret array indices in addition to dot notation.
const parsePath = (path: string): (string | number)[] => {
  const regex = /(\w+|\d+)|\[(\d+)\]/g;
  const keys: (string | number)[] = [];
  let match: RegExpExecArray | null;

  while ((match = regex.exec(path)) !== null) {
    keys.push(match[1] || match[2] ? match[1] || match[2] : match[0]);
  }

  return keys;
};

/**
 * Checks if path is a direct property of object.
 *
 * @param {Object} obj - The object to query.
 * @param {string} key - The path to check.
 * @returns {boolean} - Returns true if path exists, else false.
 */
export const has = (
  obj: Record<string, any>,
  path: string | (string | number)[]
): boolean => {
  if (obj == null) {
    return false;
  }

  const keys = Array.isArray(path) ? path : parsePath(path);

  let current = obj;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];

    if (!Object.prototype.hasOwnProperty.call(current, key)) {
      return false;
    }

    current = current[key];
  }

  return true;
};

/**
 * Creates a function that performs a partial deep comparison between a given object and source, returning true if the given object has equivalent property values, else false.
 *
 * @param {*} source - The object of property values to match.
 * @returns {function} - Returns the new spec function.
 */
export const matches = (source: any) => {
  return function(obj: any): boolean {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (obj[key] !== source[key]) {
          return false;
        }
      }
    }
    return true;
  };
};

/**
 * Performs a partial deep comparison between `object` and `source` to determine if `object` contains equivalent property values.
 *
 * @param {Object} object - The object to inspect.
 * @param {Object} source - The object of property values to match.
 * @returns {boolean} - Returns `true` if `object` is a match, else `false`.
 */
export const isMatch = (object: any, source: any): boolean => {
  for (let key in source) {
    if (Array.isArray(source[key]) && Array.isArray(object[key])) {
      return source[key].every((v: any) => object[key].includes(v));
    }

    return typeof source[key] === 'object'
      ? isMatch(object[key], source[key])
      : object[key] === source[key];
  }

  return JSON.stringify(object) === JSON.stringify(source);
};

/**
 * This method is like xor except that it accepts comparator which is invoked to compare elements of arrays.
 *
 * @param {Array} arr1 - The first array to inspect.
 * @param {Array} arr2 - The second array to inspect.
 * @param {function} comparator - The comparator invoked per element.
 * @returns {Array} - Returns the new array of filtered values.
 */
export const xorWith = (
  arr1: any[],
  arr2: any[],
  comparator: (a: any, b: any) => boolean
) => {
  let result: any[] = [];
  const combined = [...arr1, ...arr2];

  combined.forEach(item => {
    const foundInArr1 = arr1.some(a => comparator(a, item));
    const foundInArr2 = arr2.some(b => comparator(b, item));

    if (foundInArr1 !== foundInArr2) {
      result.push(item);
    }
  });

  return result;
};

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed since the last time the debounced function was invoked.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 * @param {boolean} leading - Specify invoking on the leading edge of the timeout.
 * @return {Function} - Returns the new debounced function.
 */
export const debounce = (func, wait, leading = false) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    if (leading && !timeout) {
      func.apply(this, args);
    }
    timeout = setTimeout(() => {
      timeout = null;
      if (!leading) {
        func.apply(this, args);
      }
    }, wait);
  };
};

/**
 * Recursively clones value.
 *
 * @param {*} value - The value to recursively clone.
 * @returns {*} - Returns the deep cloned value.
 */
export const cloneDeep = (value: any): any => {
  // Check for null or undefined values
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  if (value instanceof RegExp) {
    return new RegExp(value);
  }

  if (Array.isArray(value)) {
    return value.map(item => cloneDeep(item)); // Recursively clone each item in the array
  }

  const clonedObject: Record<string, unknown> = {};
  for (const key in value) {
    if (value.hasOwnProperty(key)) {
      clonedObject[key] = cloneDeep((value as Record<string, unknown>)[key]); // Recursively clone each property
    }
  }

  return clonedObject;
};
